/**
 * Tool Session Details Modal
 * Shows detailed analytics for a specific tool's usage and sessions
 */

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Activity, Users, TrendingUp, Clock, Calendar } from 'lucide-react';
import garageAPI from '../../../services/garage/api';
import '../AIGarage.css';

const ToolSessionDetailsModal = ({ isOpen, tool, filters, dateRangeLabel, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && tool) {
      loadToolStats();
    }
  }, [isOpen, tool, filters]);

  const loadToolStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await garageAPI.getToolStats(tool.toolId, filters || {});
      setStats(data);
    } catch (err) {
      console.error('Failed to load tool stats:', err);
      setError('Failed to load session details');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const formatNumber = (num) => {
    if (num === 0 || num === '0') return '0';
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const sessionsInRange = stats?.sessionsInRange ?? stats?.sessions7d ?? 0;
  const usersInRange = stats?.uniqueUsersInRange ?? stats?.uniqueUsers7d ?? 0;
  const rangeLabel = dateRangeLabel || 'Selected range';

  const modalContent = (
    <div className="garage-modal-overlay" onClick={onClose}>
      <div className="garage-modal-container" style={{ maxWidth: '800px' }} onClick={(e) => e.stopPropagation()}>
        <div className="garage-modal-header">
          <div>
            <h2 className="garage-modal-title">Session Analytics</h2>
            <p style={{ fontSize: '14px', color: 'var(--garage-text-3)', marginTop: '4px' }}>
              {tool?.toolTitle}
            </p>
          </div>
          <button className="garage-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="garage-modal-body">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p style={{ color: 'var(--garage-text-3)' }}>Loading session details...</p>
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p style={{ color: 'var(--garage-red)' }}>⚠️ {error}</p>
              <button className="garage-btn garage-btn-secondary" onClick={loadToolStats} style={{ marginTop: '16px' }}>
                Retry
              </button>
            </div>
          ) : (
            <>
              <div className="garage-dashboard-kpis" style={{ marginBottom: '24px' }}>
                <div className="garage-kpi-card">
                  <div className="garage-kpi-header">
                    <span className="garage-kpi-label">Sessions ({rangeLabel})</span>
                    <Activity size={18} style={{ color: 'var(--garage-blue)' }} />
                  </div>
                  <span className="garage-kpi-value">{formatNumber(sessionsInRange)}</span>
                  <span className="garage-kpi-sub">Matches leaderboard count</span>
                </div>

                <div className="garage-kpi-card">
                  <div className="garage-kpi-header">
                    <span className="garage-kpi-label">All Time</span>
                    <Calendar size={18} style={{ color: 'var(--garage-orange)' }} />
                  </div>
                  <span className="garage-kpi-value">{formatNumber(stats?.sessionsTotal || 0)}</span>
                  <span className="garage-kpi-sub">Total sessions</span>
                </div>

                <div className="garage-kpi-card">
                  <div className="garage-kpi-header">
                    <span className="garage-kpi-label">Users ({rangeLabel})</span>
                    <Users size={18} style={{ color: 'var(--garage-green)' }} />
                  </div>
                  <span className="garage-kpi-value">{usersInRange}</span>
                  <span className="garage-kpi-sub">Unique users</span>
                </div>

                <div className="garage-kpi-card">
                  <div className="garage-kpi-header">
                    <span className="garage-kpi-label">All Users</span>
                    <TrendingUp size={18} style={{ color: 'var(--garage-purple)' }} />
                  </div>
                  <span className="garage-kpi-value">{stats?.uniqueUsersTotal || 0}</span>
                  <span className="garage-kpi-sub">Total unique</span>
                </div>
              </div>

              {stats?.rating > 0 && (
                <div style={{
                  padding: '16px',
                  backgroundColor: 'var(--garage-bg-2)',
                  borderRadius: '8px',
                  marginBottom: '16px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '24px' }}>⭐</span>
                    <div>
                      <div style={{ fontSize: '18px', fontWeight: '600' }}>
                        {stats.rating.toFixed(1)} / 5.0
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--garage-text-3)' }}>
                        Based on {stats.ratingCount} {stats.ratingCount === 1 ? 'review' : 'reviews'}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {stats?.lastUpdated && (
                <div style={{ fontSize: '12px', color: 'var(--garage-text-3)', textAlign: 'center' }}>
                  <Clock size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                  Last updated: {new Date(stats.lastUpdated).toLocaleString()}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default ToolSessionDetailsModal;
